"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { MapPin, CircleCheck as CheckCircle2, ArrowRight, UserCheck, ShieldCheck, TrendingUp, Award, Calendar, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Link } from "@/components/common/Link";
const trustHighlights = [{
  id: 1,
  title: "Direct Consultant Access",
  desc: "Work 1-on-1 directly with Yassine—no account managers or delegates.",
  icon: UserCheck
}, {
  id: 2,
  title: "Stoke & Staffordshire Focus",
  desc: "Dominant local search authority & Google Map Pack strategy.",
  icon: MapPin
}, {
  id: 3,
  title: "Transparent & Honest ROI",
  desc: "Clear monthly roadmaps, measurable rankings, and genuine leads.",
  icon: ShieldCheck
}];
const keyMetrics = [{
  label: "Local Map Rankings",
  value: "#1 Focus"
}, {
  label: "Direct Consultant",
  value: "100% Yassine"
}, {
  label: "Strategy Style",
  value: "Tailored & Ethical"
}];
export default function Localhero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };
  return <section id="localheroauthority" data-nav="dark" className="relative min-h-[90vh] bg-primary text-primary-foreground overflow-hidden flex items-center justify-center py-16 lg:py-24">
      {/* Background Ambient Glows */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-accent/20 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-secondary/10 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary-foreground/5 rounded-full blur-3xl" />
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Column: Value Prop & Authority Pitch */}
          <motion.div className="lg:col-span-7 flex flex-col items-start" variants={containerVariants} initial="hidden" animate="visible">
            {/* Pill Badge */}
            <motion.div variants={itemVariants}>
              <Badge variant="outline" className="mb-6 px-3.5 py-1.5 text-xs sm:text-sm font-medium border-accent/40 bg-accent/10 text-primary-foreground backdrop-blur-sm rounded-full flex items-center gap-2 shadow-xs">
                <Sparkles className="w-4 h-4 text-accent" />
                <span>Local SEO Consultant in Stoke-on-Trent</span>
              </Badge>
            </motion.div>

            {/* Main Headline */}
            <motion.h1 variants={itemVariants} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-primary-foreground leading-[1.15] mb-6">
              Professional SEO Consultant for Sustainable Search Growth
            </motion.h1>

            {/* Body Copy */}
            <motion.p variants={itemVariants} className="font-sans text-base sm:text-lg text-primary-foreground/90 max-w-2xl font-normal leading-relaxed mb-8">
              I help businesses in Stoke-on-Trent, Staffordshire, and across the UK dominate local search results and capture high-intent leads. When you partner with me, you work directly with Yassine Essaadi—no agency middlemen, no generic templates, and no outsourced tasks.
            </motion.p>

            {/* CTAs */}
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto mb-10">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-lg font-semibold h-12 px-7 text-base rounded-lg transition-all transform hover:-translate-y-0.5">
                <Link to="/ContactPage">
                  <span>Book a Free SEO Consultation</span>
                  <SafeHydrate><Calendar className="w-4 h-4 ml-2" /></SafeHydrate>
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 h-12 px-6 text-base font-medium rounded-lg backdrop-blur-xs transition-all">
                <Link to="/CaseStudiesPage">
                  <span>Explore My Case Studies</span>
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </motion.div>

            {/* Trust Highlights Grid */}
            <motion.div variants={itemVariants} className="w-full pt-6 border-t border-primary-foreground/15 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {trustHighlights.map((item, i) => {
              const IconComp = item.icon;
              return <div key={item.id} data-index={i} className="flex items-start space-x-3">
                    <div className="p-2 rounded-md bg-primary-foreground/10 shrink-0 mt-0.5">
                      <IconComp className="w-4 h-4 text-accent" />
                    </div>
                    <div>
                      <h4 className="font-serif text-xs font-semibold text-primary-foreground uppercase tracking-wider">
                        {item.title}
                      </h4>
                      <p className="font-sans text-xs text-primary-foreground/80 mt-0.5 leading-snug">
                        {item.desc}
                      </p>
                    </div>
                  </div>;
            })}
            </motion.div>
          </motion.div>

          {/* Right Column: Expert Profile & Local Signal Card */}
          <motion.div className="lg:col-span-5 relative" initial={{
          opacity: 0,
          scale: shouldReduceMotion ? 1 : 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.7,
          delay: 0.2
        }}>
            <div className="relative mx-auto max-w-md lg:max-w-none">
              
              {/* Outer decorative card frame */}
              <div className="absolute -inset-1.5 rounded-2xl bg-gradient-to-tr from-accent/40 to-primary-foreground/20 blur-lg opacity-70" />

              {/* Main Card Surface */}
              <div className="relative rounded-2xl bg-card text-card-foreground p-4 sm:p-6 shadow-2xl border border-border">
                
                {/* Consultant Visual / Location badge */}
                <div className="relative aspect-4/5 sm:aspect-4/3 lg:aspect-4/5 rounded-xl overflow-hidden bg-muted mb-6">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-sizes" alt="Yassine Essaadi - Local SEO Consultant in Stoke-on-Trent" className="w-full h-full object-cover object-center" loading="eager" />
                  
                  {/* Floating Location Badge */}
                  <div className="absolute top-4 left-4 bg-background/95 backdrop-blur-md text-foreground px-3 py-2 rounded-lg shadow-md border border-border flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-accent" />
                    <div className="text-xs">
                      <span className="font-bold block text-foreground">Stoke-on-Trent</span>
                      <span className="text-muted-foreground text-[11.5px]">Staffordshire & UK</span>
                    </div>
                  </div>

                  {/* Floating Consultant Badge */}
                  <div className="absolute bottom-4 right-4 bg-primary text-primary-foreground px-3.5 py-2.5 rounded-lg shadow-lg border border-primary-foreground/20 flex items-center gap-2.5">
                    <SafeHydrate><Avatar className="h-7 w-7 border border-primary-foreground/30">
                      <AvatarImage src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_avatar.png" alt="Yassine Essaadi" />
                      <AvatarFallback className="bg-accent text-accent-foreground font-bold text-xs">YE</AvatarFallback>
                    </Avatar></SafeHydrate>
                    <div>
                      <span className="font-serif text-xs font-semibold block leading-none">Yassine Essaadi</span>
                      <span className="text-[11.5px] text-primary-foreground/80 leading-tight">Independent SEO Expert</span>
                    </div>
                  </div>
                </div>

                {/* Key Metrics Grid */}
                <div className="grid grid-cols-3 gap-2 pt-2 border-t border-border text-center">
                  {keyMetrics.map((metric, i) => <div key={metric.label} data-index={i} className="px-1 py-2 rounded-lg bg-muted/60">
                      <span className="block font-serif text-xs font-bold text-foreground">
                        {metric.value}
                      </span>
                      <span className="text-[11.5px] text-muted-foreground font-medium">
                        {metric.label}
                      </span>
                    </div>)}
                </div>

                {/* Direct Commitment Footer */}
                <div className="mt-4 p-3 rounded-lg bg-secondary/30 border border-secondary/50 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2 text-foreground font-medium">
                    <CheckCircle2 className="w-4 h-4 text-accent shrink-0" />
                    <span>No Agency Overhead. 100% Direct Partnering.</span>
                  </div>
                  <Button asChild variant="ghost" size="sm" className="h-7 px-2 text-xs font-semibold text-primary hover:text-primary/80 hover:bg-transparent">
                    <Link to="/AboutMePage">About Me</Link>
                  </Button>
                </div>

              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}